import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Product {
    id: string;
    title: string;
    main_image: string;
    price_usd: string;
    price_eur: string;
    price_rub: string;
    price_cny: string;
    tags: string[];
    created_at: string;
}

interface ProductGridProps {
    selectedFilters: string[];
}

const ProductGrid: React.FC<ProductGridProps> = ({ selectedFilters }) => {
    const [products, setProducts] = useState<Product[]>([]);
    const [showTooltip, setShowTooltip] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/products`)
            .then((response) => response.json())
            .then((data) => setProducts(data))
            .catch((error) => console.error('Error fetching products:', error));
    }, []);

    const isNew = (createdAt: string) => {
        const createdDate = new Date(createdAt);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 14);
        return createdDate >= thirtyDaysAgo;
    };

    const handleProductClick = (id: string) => {
        navigate(`/product/${id}`);
    };

    const filteredProducts = products.filter((product) => {
        const productTagsLower = product.tags.map((tag) => tag.toLowerCase());
        return selectedFilters.every((filter) => productTagsLower.includes(filter));
    });

    return (
        <div className="flex flex-wrap gap-8 justify-center">
            {filteredProducts.length === 0 ? (
                <div className="text-center text-2xl text-slate-400 mt-3 uppercase font-bold" style={{ fontFamily: 'Montserrat, serif' }}>
                    Nothing Found 😿
                </div>
            ) : (
                filteredProducts.map((product) => (
                    <div
                        key={product.id}
                        className="relative w-[290px] h-[320px] bg-gradient-to-r from-slate-700 to-gray-700 p-2 rounded-md shadow-xl cursor-pointer flex flex-col justify-between hover:shadow-[0_0_10px_2px_rgba(203,213,225,0.5)] transition duration-500"
                        onClick={() => handleProductClick(product.id)}
                    >
                        {isNew(product.created_at) && (
                            <div
                                className="absolute top-2 left-2 transform -translate-x-1/3 -translate-y-1/2 bg-red-800/85 text-white font-bold px-2 py-1 rounded shadow-md text-md"
                                style={{ fontFamily: 'Montserrat, serif' }}
                            >
                                NEW
                            </div>
                        )}

                        <div
                            className="absolute top-2 right-2 transform translate-x-1/3 -translate-y-1/2 bg-yellow-400/85 text-black font-bold px-2 py-1 rounded shadow-md text-md"
                            style={{ fontFamily: 'Montserrat, serif' }}
                            onMouseEnter={() => setShowTooltip(product.id)}
                            onMouseLeave={() => setShowTooltip(null)}
                        >
                            {product.price_usd}$
                            {showTooltip === product.id && (
                                <div
                                    className="absolute top-full right-0 mt-1 w-fit bg-gray-900/85 text-white text-sm rounded p-2 shadow-lg z-10 text-right"
                                    style={{ fontFamily: 'Montserrat, serif' }}
                                >
                                    <div>{product.price_eur}€</div>
                                    <div>{product.price_rub}₽</div>
                                    <div>{product.price_cny}¥</div>
                                </div>
                            )}
                        </div>

                        <img
                            src={product.main_image}
                            alt={product.title}
                            className="w-full object-cover rounded mb-1"
                        />

                        <h3
                            className="text-xl font-bold text-center text-slate-100"
                            style={{ fontFamily: 'Tektur, serif' }}
                        >
                            {product.title}
                        </h3>
                    </div>
                ))
            )}
        </div>
    );
};

export default ProductGrid;